import { toast } from 'sonner';

import { useRegion } from '@/context';
import { useResendVerificationEmail } from '@/hooks/use-current-user';
import { errorIds, logError } from '@/lib/log-error';
import { ApiError } from '@/services/api-client';

/**
 * The verification-email resend flow, shared by the account banner and the
 * device-use gate (ESCLOUD-333). The token minted at sign-in cannot know
 * about a click in the inbox, so an "already verified" refusal is progress,
 * not an error: it refreshes the token instead of surfacing a failure toast,
 * and lets the caller react to the newly-proven address.
 */
export function useVerificationResend(options: { email: string; onAlreadyVerified?: () => void }): {
    resend: () => Promise<void>;
    isPending: boolean;
} {
    const { email, onAlreadyVerified } = options;
    const { refreshToken } = useRegion();
    const resend = useResendVerificationEmail();

    const run = async () => {
        try {
            await resend.mutateAsync();
            toast.success('Verification email requested', {
                description: `Check ${email}, and the spam folder.`,
            });
        } catch (error) {
            if (error instanceof ApiError && error.errorCode === 'email_already_verified') {
                await refreshToken().catch(() => undefined);
                onAlreadyVerified?.();
                return;
            }
            logError(errorIds.VERIFICATION_EMAIL_REQUEST_FAILED, 'Verification email resend failed', {
                error,
            });
            toast.error('The verification email could not be requested', {
                description:
                    error instanceof ApiError && error.status === 429
                        ? 'Too many requests. Try again in an hour.'
                        : 'Please try again.',
            });
        }
    };

    return { resend: run, isPending: resend.isPending };
}
